import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { useMemo } from 'react';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { getContentById } from '@/utils/api/content/content-api';
import {
    getFutureShowings,
    type GetFutureShowingsQuery as GetFutureShowingsServerQuery
} from '@/utils/api/showings/showing-api';
import { UrlQueryParameter } from '@cineamo/api/helpers/url-query-helper';
import { getMovieById } from '@cineamo/api/resources/movie/movie-v2-api';
import { ShowingApiGroup } from '@cineamo/api/resources/showing/showing-api.group';

import { getDateInTimezone } from '@/utils/date-helper';

const getQueryKey = (query: Record<string, unknown> | null | undefined): string[] => {
    return Object.entries(query ?? {}).map(([key, value]) => `${key}-${JSON.stringify(value)}`);
};

export type GetFutureShowingsQuery = {
    contentId?: string | number;
    /** @deprecated cinema id must be in a first argument of a function and it must always be a single value, not array */
    cinemaId?: never;
    startDatetime?: string;
    endDatetime?: string;
    movieId?: MovieDto['id'];
    hasMovieId?: boolean;
    name?: string;
    isEventRequest?: boolean;
    isThreeDimensional?: boolean;
    group?: ShowingApiGroup;
    /**
     * @deprecated fetch related entities with separate API calls
     */
    embed?: never;
    cinemaRoomIds?: number[] | string[];
    language?: string;
    subtitledLanguage?: string;
    isSubtitled?: boolean;
    isDubbed?: boolean;
    dubbedLanguage?: string;
    isOriginalLanguage?: boolean;
    isDolbyAtmos?: boolean;
    isImax?: boolean;
    isDolbyVision?: boolean;
    isDolbyCinema?: boolean;
    is4DX?: boolean;
    isScreenX?: boolean;
    isHFR?: boolean;
    isLive?: boolean;
    isDbox?: boolean;
    isHighlightAfterDatetime?: string;
    startTime?: string;
    endTime?: string;
} & UrlQueryParameter;

/**
 * Hook that implements two-layer TanStack Query caching for getFutureShowings:
 * 1. First layer: Cache the HTTP response based on cinemaId + serverQuery
 * 2. Second layer: Cache the filtered result based on clientQuery (local filtering)
 *
 * This prevents duplicate HTTP requests when different client filters are applied
 * to the same cinema's showings data.
 */
export function useFutureShowings(
    cinemaId: CinemaDto['id'] | string,
    clientQuery?: GetFutureShowingsQuery | null,
    serverQuery?: GetFutureShowingsServerQuery | null,
    options?: {
        enabled?: boolean;
        staleTime?: number;
        keepPreviousData?: boolean;
        initialData?: ShowingDto[];
        initialDataUpdatedAt?: number;
    }
) {
    const locale = useLocale();

    // Generate stable keys for caching
    const serverQueryKey = getQueryKey(serverQuery);
    const clientQueryKey = getQueryKey(clientQuery);

    // Layer 1: HTTP request cache - only depends on cinemaId and serverQuery
    const rawQuery = useQuery({
        queryKey: [`cinemas/${cinemaId}/showings-future`, ...serverQueryKey],
        queryFn: async () => {
            const showingsResponse = await getFutureShowings(cinemaId, serverQuery);
            return showingsResponse?._embedded?.showings || [];
        },
        enabled: options?.enabled !== false,
        staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes default
        // keepPreviousData: options?.keepPreviousData,
        initialData: options?.initialData,
        initialDataUpdatedAt: options?.initialDataUpdatedAt
    });
    const { data: rawShowings, isLoading: isLoadingRaw, error: rawError } = rawQuery;

    // Filtering is only genuinely async for the ShowingApiGroup.Content path (per-showing
    // content/movie enrichment). Every other query is pure array filtering — computing it
    // synchronously keeps the filtered result available during SSR (an async layer-2 query
    // can never resolve within the single server render pass, which previously left the
    // server HTML without any showings).
    const needsAsyncFiltering = clientQuery?.group === ShowingApiGroup.Content;
    const clientQueryKeyString = clientQueryKey.join('|');

    const syncFilteredShowings = useMemo(() => {
        if (needsAsyncFiltering || !rawShowings) return undefined;
        return applyClientFiltersSync(rawShowings, clientQuery);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- clientQuery is represented by its stable key string
    }, [needsAsyncFiltering, rawShowings, clientQueryKeyString]);

    // Layer 2: Filtered result cache - only for the async enrichment path (group by content)
    const {
        data: filteredResponse,
        isLoading: isLoadingFiltered,
        error: filteredError
    } = useQuery({
        queryKey: [`cinemas/${cinemaId}/showings-future-filtered`, ...serverQueryKey, ...clientQueryKey],
        queryFn: async () => {
            if (!rawShowings) return null;

            // Apply client-side filtering to cached raw response
            return await applyClientFilters(rawShowings, clientQuery, { locale, cinemaId });
        },
        enabled: options?.enabled !== false && !!rawShowings && needsAsyncFiltering,
        staleTime: options?.staleTime ?? 5 * 60 * 1000 // 5 minutes default
    });

    // Stable reference for empty/loading state to prevent cascading re-renders
    // through useMemo dependency chains (showingsCount → sortFunction → filteredContents)
    const data = useMemo(
        () => (needsAsyncFiltering ? filteredResponse : syncFilteredShowings) || [],
        [needsAsyncFiltering, filteredResponse, syncFilteredShowings]
    );

    return {
        data,
        isLoading: isLoadingRaw || (needsAsyncFiltering && isLoadingFiltered),
        error: rawError || (needsAsyncFiltering ? filteredError : null)
    };
}

/**
 * Synchronous part of the client-side filtering: pure filters + datetime sort.
 * Safe to run inside a render pass (SSR included) — used for every query that does
 * not need the async per-showing enrichment of ShowingApiGroup.Content.
 */
export function applyClientFiltersSync(
    showings: ShowingDto[] = [],
    query?: GetFutureShowingsQuery | null
): ShowingDto[] {
    if (!query) {
        return showings;
    }

    const filters = getShowingFilters(query);
    let filtered = showings.filter((showing) => filters.every((filter) => filter(showing)));

    if ('sort' in query) {
        const order = query.order === 'asc' ? 1 : -1;
        filtered = [...filtered].sort(
            (a, b) => order * (new Date(a.startDatetime).getTime() - new Date(b.startDatetime).getTime())
        );
    }

    return filtered;
}

/**
 * Apply client-side filters to the raw showings response
 * This contains all the filtering logic that was previously in getFutureShowings
 */
export async function applyClientFilters(
    showings: ShowingDto[] = [],
    query?: GetFutureShowingsQuery | null,
    options?: { locale?: string; cinemaId?: number | string }
) {
    if (!query) {
        return showings;
    }

    if (query.group !== ShowingApiGroup.Content) {
        return applyClientFiltersSync(showings, query);
    }

    const filters = getShowingFilters(query);
    showings = showings.filter((showing) => filters.every((filter) => filter(showing)));

    const showingsByContentIdCount = new Map<ShowingDto['contentId'], number>();
    if (query.group === ShowingApiGroup.Content) {
        const uniqueShowings = showings.filter((showing) => {
            const previousCount = showingsByContentIdCount.get(showing.contentId) || 0;
            showingsByContentIdCount.set(showing.contentId, previousCount + 1);
            return previousCount === 0;
        });

        // Batch processing to prevent stack overflow while preserving order
        const BATCH_SIZE = 20;
        const enrichedShowings = [];

        for (let i = 0; i < uniqueShowings.length; i += BATCH_SIZE) {
            const batch = uniqueShowings.slice(i, i + BATCH_SIZE);
            const batchResults = await Promise.all(
                batch.map(async (showing) => {
                    const content = await getContentById(showing.contentId);
                    const movie =
                        content?.movieId && options?.locale && options?.cinemaId
                            ? await getMovieById(content.movieId, {
                                  locale: options.locale,
                                  cinemaId: options.cinemaId,
                                  include: 'images'
                              })
                            : undefined;
                    return {
                        ...showing,
                        _embedded: {
                            ...showing._embedded,
                            content: content ?? undefined,
                            movie: movie ?? undefined
                        }
                    };
                })
            );
            enrichedShowings.push(...batchResults);
        }

        showings = enrichedShowings as ShowingDto[];
    }

    if ('sort' in query) {
        if (query.sort === 'popularity' && query.group === ShowingApiGroup.Content) {
            showings = showings.sort((a, b) => {
                return (
                    (showingsByContentIdCount.get(b._embedded?.content?.id) ?? 0) -
                    (showingsByContentIdCount.get(a._embedded?.content?.id) ?? 0)
                );
            });
        } else {
            showings = showings.sort((a, b) => {
                const order = query.order === 'asc' ? 1 : -1;
                return order * (new Date(a.startDatetime).getTime() - new Date(b.startDatetime).getTime());
            });
        }
    }

    return showings;
}

/**
 * Generate filters based on query parameters
 * This replicates the filtering logic from showing-api.ts
 */
export function getShowingFilters(query: GetFutureShowingsQuery) {
    const hasQueryValue = (key: keyof GetFutureShowingsQuery) => {
        return key in query && query[key] !== undefined && query[key] !== null;
    };

    const getBoolean = (val: boolean | string | undefined) => {
        return typeof val === 'boolean' ? val : val === 'true';
    };

    const toDate = (val: string | undefined) => (val !== undefined ? new Date(val) : undefined);

    const highlightDate = 'isHighlightAfterDatetime' in query ? toDate(query.isHighlightAfterDatetime) : undefined;
    const startDatetime = 'startDatetime' in query ? toDate(query.startDatetime) : undefined;
    const endDatetime = 'endDatetime' in query ? toDate(query.endDatetime) : undefined;
    const cinemaRoomIds =
        'cinemaRoomIds' in query
            ? Array.isArray(query.cinemaRoomIds)
                ? query.cinemaRoomIds.map(Number)
                : [Number(query.cinemaRoomIds)]
            : [];

    const filters: ((showing: ShowingDto) => boolean)[] = [];

    if (hasQueryValue('startTime'))
        filters.push((showing) => getDateInTimezone(showing.startDatetime).getHours() >= Number(query.startTime));
    if (hasQueryValue('endTime'))
        filters.push((showing) => getDateInTimezone(showing.startDatetime).getHours() <= Number(query.endTime));
    if (hasQueryValue('movieId')) filters.push((showing) => showing.movieId === query.movieId);
    if (hasQueryValue('hasMovieId')) filters.push((showing) => !!showing.movieId);
    if (hasQueryValue('contentId')) filters.push((showing) => showing.contentId === Number(query.contentId));
    if (hasQueryValue('name'))
        filters.push((showing) => !!showing.name?.toLowerCase().includes(query.name?.toLowerCase() ?? ''));
    if (hasQueryValue('startDatetime') && startDatetime)
        filters.push((showing) => new Date(showing.startDatetime) >= startDatetime);
    if (hasQueryValue('endDatetime') && endDatetime)
        filters.push((showing) => new Date(showing.startDatetime) <= endDatetime);
    if (hasQueryValue('cinemaRoomIds'))
        filters.push((showing) => showing.cinemaRoomId !== undefined && cinemaRoomIds.includes(showing.cinemaRoomId));
    if (hasQueryValue('language')) filters.push((showing) => showing.language === query.language);
    if (hasQueryValue('subtitledLanguage'))
        filters.push((showing) => showing.subtitledLanguage === query.subtitledLanguage);
    if (hasQueryValue('dubbedLanguage')) filters.push((showing) => showing.dubbedLanguage === query.dubbedLanguage);
    if (hasQueryValue('isOriginalLanguage'))
        filters.push((showing) => showing.isOriginalLanguage === getBoolean(query.isOriginalLanguage));
    if (hasQueryValue('isHighlightAfterDatetime'))
        filters.push(
            (showing) =>
                !!showing.highlightAfterDatetime &&
                !!highlightDate &&
                new Date(showing.highlightAfterDatetime) <= highlightDate
        );
    if (hasQueryValue('isHighlighted'))
        filters.push(
            (showing) => !!showing.highlightAfterDatetime && new Date(showing.highlightAfterDatetime) <= new Date()
        );
    if (hasQueryValue('isEventRequest')) filters.push((showing) => !!showing.eventRequestId);
    if (hasQueryValue('isThreeDimensional'))
        filters.push((showing) => Boolean(showing.isThreeDimensional) === getBoolean(query.isThreeDimensional));
    if (hasQueryValue('isSubtitled')) filters.push((showing) => showing.isSubtitled === getBoolean(query.isSubtitled));
    if (hasQueryValue('isDubbed')) filters.push((showing) => showing.isDubbed === getBoolean(query.isDubbed));
    if (hasQueryValue('isDolbyAtmos'))
        filters.push((showing) => showing.isDolbyAtmos === getBoolean(query.isDolbyAtmos));
    if (hasQueryValue('isImax')) filters.push((showing) => showing.isImax === getBoolean(query.isImax));
    if (hasQueryValue('isDolbyVision'))
        filters.push((showing) => showing.isDolbyVision === getBoolean(query.isDolbyVision));
    if (hasQueryValue('isDolbyCinema'))
        filters.push((showing) => showing.isDolbyCinema === getBoolean(query.isDolbyCinema));
    if (hasQueryValue('is4DX')) filters.push((showing) => showing.is4DX === getBoolean(query.is4DX));
    if (hasQueryValue('isScreenX')) filters.push((showing) => showing.isScreenX === getBoolean(query.isScreenX));
    if (hasQueryValue('isHFR')) filters.push((showing) => showing.isHFR === getBoolean(query.isHFR));
    if (hasQueryValue('isLive')) filters.push((showing) => showing.isLive === getBoolean(query.isLive));
    if (hasQueryValue('isDbox')) filters.push((showing) => showing.isDbox === getBoolean(query.isDbox));

    return filters;
}

export async function getFutureShowingsFiltered(
    cinemaId: CinemaDto['id'] | string,
    clientQuery?: GetFutureShowingsQuery | null,
    serverQuery?: GetFutureShowingsServerQuery | null,
    options?: { locale?: string }
) {
    if ((clientQuery?.showingTagIds as string | string[])?.length > 0) {
        serverQuery = {
            ...serverQuery,
            showingTagIds: Array.isArray(clientQuery?.showingTagIds)
                ? clientQuery?.showingTagIds.map(Number)
                : [Number(clientQuery?.showingTagIds)]
        };
    }

    if (clientQuery && 'hasShowingTags' in clientQuery && clientQuery.hasShowingTags !== undefined) {
        serverQuery = {
            ...serverQuery,
            hasShowingTags: clientQuery.hasShowingTags === 'true'
        };
    }

    const rawShowingsResponse = await getFutureShowings(cinemaId, serverQuery);
    const showings = rawShowingsResponse?._embedded?.showings || [];
    return await applyClientFilters(showings, clientQuery, { locale: options?.locale, cinemaId });
}
